'use client';

import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import type { AreaData, CoastalRegion } from '@routeroyal/theme-core';

import { StoneAreaMapThumbnail } from './StoneAreaMapThumbnail';

const AREAS_HERO_GRADIENT = 'from-teal-900 via-cyan-800 to-stone-900';

interface RegionWithAreas {
  region: CoastalRegion;
  areas: Array<{
    slug: string;
    name: string;
    data?: AreaData;
  }>;
}

export interface StoneAreasIndexMessages {
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  cantDecideTitle?: string;
  cantDecideDescription?: string;
  cantDecideCta?: string;
}

export interface StoneAreasIndexProps {
  regions: RegionWithAreas[];
  locale: string;
  messages?: StoneAreasIndexMessages;
}

export function StoneAreasIndex({
  regions,
  locale,
  messages,
}: StoneAreasIndexProps) {
  const isFr = locale === 'fr';

  const eyebrow = messages?.eyebrow || (isFr ? 'Nos Destinations' : 'Our Locations');
  const title = messages?.title || (isFr ? 'Découvrez l\'Île Maurice' : 'Discover Mauritius');
  const subtitle =
    messages?.subtitle ||
    (isFr
      ? 'Des côtes nord animées aux rivages préservés du sud, explorez les plus belles adresses de l\'île.'
      : 'From the vibrant north coast to the pristine southern shores, explore the island\'s finest addresses.');
  const ctaTitle =
    messages?.cantDecideTitle || (isFr ? 'Besoin de Conseils ?' : 'Need Guidance?');
  const ctaDescription =
    messages?.cantDecideDescription ||
    (isFr
      ? 'Nos conseillers connaissent chaque quartier de l\'Île Maurice. Laissez-nous vous orienter vers la localisation idéale.'
      : 'Our advisors know every corner of Mauritius. Let us guide you to the perfect location for your lifestyle and investment goals.');
  const ctaButton = messages?.cantDecideCta || (isFr ? 'Contactez-nous' : 'Contact Us');

  return (
    <div className="min-h-screen bg-stone-50 font-sans text-stone-900">
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[450px] max-h-[700px] overflow-hidden">
        <div className={`absolute inset-0 bg-gradient-to-br ${AREAS_HERO_GRADIENT}`} />

        <div className="absolute inset-0 flex items-end">
          <div className="container mx-auto max-w-7xl px-6 md:px-12 pb-16 md:pb-24">
            <p className="text-[10px] uppercase tracking-[0.3em] text-stone-300 mb-4 font-sans">
              {eyebrow}
            </p>
            <h1
              className="font-serif text-4xl md:text-6xl text-white italic leading-tight mb-4"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              {title}
            </h1>
            <p className="text-stone-200 text-lg font-light max-w-2xl leading-relaxed font-sans">
              {subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Regions */}
      <section className="py-16 md:py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {regions.map((regionGroup) => (
            <div key={regionGroup.region} className="mb-16 md:mb-24 last:mb-0">
              {/* Region heading */}
              <div className="mb-10">
                <h2
                  className="font-serif text-2xl md:text-3xl text-stone-900 italic"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                >
                  {regionGroup.region}
                </h2>
                <div className="mt-3 h-px bg-stone-200 w-full" />
              </div>

              {/* Area cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {regionGroup.areas.map((area) => (
                  <Link
                    key={area.slug}
                    href={`/${locale}/areas/${area.slug}`}
                    className="group bg-white shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
                  >
                    {/* Map Thumbnail */}
                    <div className="relative h-52 overflow-hidden">
                      {area.data?.latitude && area.data?.longitude ? (
                        <StoneAreaMapThumbnail
                          latitude={area.data.latitude}
                          longitude={area.data.longitude}
                        />
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-stone-700 to-stone-900" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-stone-900/50 via-transparent to-transparent pointer-events-none" />

                      {area.data?.priceRange && (
                        <div className="absolute top-4 right-4 bg-white/95 px-3 py-1 text-[10px] uppercase tracking-wider font-sans text-stone-700 z-10">
                          {area.data.priceRange}
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-2">
                        <h3
                          className="font-serif text-xl text-stone-900 italic group-hover:text-stone-600 transition-colors"
                          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                        >
                          {area.name}
                        </h3>
                        <ArrowRight
                          size={16}
                          strokeWidth={1}
                          className="text-stone-300 group-hover:text-stone-600 group-hover:translate-x-1 transition-all mt-1.5 flex-shrink-0"
                        />
                      </div>

                      {area.data?.description && (
                        <p className="text-stone-500 text-sm font-light leading-relaxed line-clamp-2 font-sans">
                          {area.data.description.split('.')[0]}.
                        </p>
                      )}

                      {area.data?.highlights && area.data.highlights.length > 0 && (
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-4 pt-4 border-t border-stone-100">
                          {area.data.highlights.slice(0, 3).map((highlight, idx) => (
                            <span
                              key={idx}
                              className="text-[10px] uppercase tracking-wider text-stone-400 font-sans"
                            >
                              {highlight}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-stone-900 py-20 md:py-28 px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <h2
            className="font-serif text-3xl md:text-4xl text-white italic mb-6"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            {ctaTitle}
          </h2>
          <p className="text-stone-300 text-lg font-light leading-relaxed mb-10 font-sans">
            {ctaDescription}
          </p>
          <Link
            href={`/${locale}/contact?intent=consultation`}
            className="inline-block px-12 py-5 bg-white text-stone-900 text-xs uppercase tracking-widest font-sans hover:bg-stone-200 transition-all duration-500 shadow-2xl"
          >
            {ctaButton}
          </Link>
        </div>
      </section>
    </div>
  );
}
